/**
 * "Leave site?" when closing the tab would actually cost something.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THE BROWSER WILL AND WILL NOT DO
 *
 * The only mechanism is `beforeunload`, and it has three hard constraints that
 * shape everything below. None of them can be worked around.
 *
 *   1. THE TEXT IS NOT OURS. Browsers stopped honouring a custom message
 *      around 2016 because scareware used it ("your computer is infected,
 *      click Cancel"). Chrome says "Changes you made may not be saved", Safari
 *      says something else, and there is no API to change either. So the
 *      reasons collected here are for OUR logs and for deciding WHETHER to
 *      prompt — the user never reads them in the dialog.
 *
 *   2. IT NEEDS STICKY ACTIVATION. Chrome refuses to show the dialog at all
 *      unless the user has interacted with the page — a click, a key, a tap.
 *      A terminal that was opened and never touched closes silently, and that
 *      is the browser's decision, not a bug here.
 *
 *   3. A REGISTERED LISTENER HARMS THE BACK/FORWARD CACHE. A page holding one
 *      is treated as ineligible or second-class for bfcache in several
 *      browsers. That is why this attaches the listener only while at least
 *      one reason is claimed and removes it the moment the last one is
 *      released, rather than registering once at boot.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT COUNTS AS A REASON HERE — AND WHAT DELIBERATELY DOES NOT
 *
 * Only state the BROWSER owns. Closing the tab must not be made to feel
 * dangerous when it is not, or the dialog becomes something people click
 * through without reading, which is worse than not having it.
 *
 * So an open POSITION is not a reason, and neither is an armed stop or
 * target. Those live in the engine: `PremiumOrderMonitor` evaluates app
 * protection from the tick feed server-side and raises its exit whether or
 * not any browser is watching. Prompting for them would teach the trader that
 * closing the tab disarms their protection, which is the opposite of true.
 *
 * What IS at risk is work that exists only in this tab: an intent that has
 * been sent and not yet acknowledged, and a ticket someone has typed into.
 * ─────────────────────────────────────────────────────────────────────────────
 */

type Listener = () => void

class LeaveGuard {
  /** key -> why. The key lets one owner replace its own claim idempotently. */
  private readonly reasons = new Map<string, string>()
  private readonly listeners = new Set<Listener>()
  private attached = false

  private readonly onBeforeUnload = (e: BeforeUnloadEvent): void => {
    if (this.reasons.size === 0) return
    /*
     * BOTH, deliberately. `preventDefault()` is what the current spec says;
     * `returnValue` is what older WebKit and Firefox still read. Setting only
     * one works in some browsers and silently does nothing in others, which
     * is the worst outcome for a guard.
     */
    e.preventDefault()
    e.returnValue = ''
  }

  private sync(): void {
    const wanted = this.reasons.size > 0
    if (wanted === this.attached) return
    if (wanted) window.addEventListener('beforeunload', this.onBeforeUnload)
    else window.removeEventListener('beforeunload', this.onBeforeUnload)
    this.attached = wanted
    for (const fn of this.listeners) fn()
  }

  /** Hold the tab for as long as this key is claimed. */
  claim(key: string, reason: string): void {
    if (this.reasons.get(key) === reason) return
    this.reasons.set(key, reason)
    this.sync()
  }

  release(key: string): void {
    if (!this.reasons.delete(key)) return
    this.sync()
  }

  /** What is currently holding the tab. For diagnostics, never for the dialog. */
  held(): string[] {
    return [...this.reasons.values()]
  }

  /** Test seam. */
  reset(): void {
    this.reasons.clear()
    this.sync()
  }

  onChange(fn: Listener): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }
}

export const leaveGuard = new LeaveGuard()
